import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { watchAPI } from '../services/api';
import axios from 'axios';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';

const AddWatchPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMessage, setLookupMessage] = useState('');
  const [stockImageUrl, setStockImageUrl] = useState(null);
  const [variants, setVariants] = useState([]);
  const [showVariants, setShowVariants] = useState(false);
  const lookupTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    reference: '',
    year: '',
    serial_number: '',
    condition: 'Excellent',
    description: '',
    case_size: '',
    case_material: '',
    movement: '',
    movement_type: '',
    bracelet_material: '',
    cost: '',
    asking_price: '',
    is_public: false,
    box: '',
    papers: '',
    city: '',
    state: '',
    country: '',
    is_consignment: false,
    consignment_owner_name: '',
    consignment_owner_email: '',
    consignment_owner_phone: '',
    consignment_owner_address: '',
    commission_percentage: '',
    consignment_agreement_date: '',
    consignment_end_date: '',
    consignment_notes: '',
    internal_notes: ''
  });

  const performLookup = useCallback(async (reference) => {
    setLookupLoading(true);
    setLookupMessage('🤖 Chrono is analyzing this reference...');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://watchdealer-crm-backend.onrender.com';

      // Use Chrono AI endpoint
      const response = await fetch(`${apiUrl}/api/chrono/recognize/${encodeURIComponent(reference)}`);

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.variants && result.variants.length > 0) {
        const foundVariants = result.variants;
        setVariants(foundVariants);

        // Auto-fill brand immediately if detected
        if (result.brand) {
          setFormData(prev => ({ ...prev, brand: result.brand }));
        }

        // Show Chrono's smart message
        let message = '';
        if (result.from_knowledge_base) {
          message = `🤖 I know this one! Found ${foundVariants.length} variants`;
        } else if (result.learned_from_web) {
          message = `🤖 Learned it from the web! Found ${foundVariants.length} variants`;
        } else {
          message = `🤖 Found ${foundVariants.length} variants`;
        }

        if (foundVariants.length > 1) {
          // Multiple variants - show selector
          setShowVariants(true);
          setLookupMessage(message + ' - pick yours!');
        } else {
          // Single variant - auto-fill immediately
          selectVariant(foundVariants[0]);
          setShowVariants(false);
          setLookupMessage(message + ' - Auto-filled!');
        }
      } else {
        // Show appropriate message based on what Chrono tried
        if (result.learned_from_web) {
          setLookupMessage('⚠️ Found partial info - please complete manually');
        } else {
          setLookupMessage('ℹ Unknown reference - enter details manually');
        }
        setVariants([]);
        setShowVariants(false);
      }
    } catch (err) {
      console.error('[Chrono] Error:', err);
      setLookupMessage('⚠ Chrono lookup failed - enter details manually');
      setVariants([]);
      setShowVariants(false);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  const selectVariant = (variant) => {
    // Chrono provides richer data - auto-fill all available fields
    setFormData(prev => {
      const updates = {
        ...prev,
        reference: variant.reference || prev.reference,
        brand: variant.brand || prev.brand,
        model: variant.model || prev.model,
        year: variant.year ? String(variant.year) : prev.year,
        case_size: variant.size || variant.case_size || prev.case_size,
        case_material: variant.material || variant.case_material || prev.case_material,
        movement: variant.movement || variant.movement_type || prev.movement,
        movement_type: variant.movement_type || prev.movement_type,
        bracelet_material: variant.bracelet_material || variant.material || prev.bracelet_material,
      };

      // Chrono extracts dial color!
      if (variant.dial_color) {
        const dialInfo = variant.dial_color;
        updates.description = prev.description
          ? `${dialInfo} dial. ${prev.description}`
          : `${dialInfo} dial`;
      } else if (variant.description) {
        updates.description = variant.description;
      }

      return updates;
    });

    // Set stock image
    if (variant.image_url) {
      setStockImageUrl(variant.image_url);
    }

    // Hide variant selector and show success message
    setShowVariants(false);
    const brandModel = variant.brand && variant.model
      ? `${variant.brand} ${variant.model}`
      : variant.brand || 'this variant';
    setLookupMessage(`✓ Chrono auto-filled ${brandModel} - Add serial number and pricing!`);
  };

  const handleReferenceChange = (e) => {
    const reference = e.target.value;

    // Update reference field immediately
    setFormData(prev => ({ ...prev, reference }));

    // Clear any existing timeout
    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }

    // Only lookup if reference is at least 4 characters
    if (reference.length >= 4) {
      // Debounce the API call - wait 500ms after user stops typing
      lookupTimeoutRef.current = setTimeout(() => {
        performLookup(reference);
      }, 500);
    } else {
      // Clear lookup state if reference is too short
      setLookupMessage('');
      setStockImageUrl(null);
      setLookupLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const costNum = parseFloat(formData.cost);
    const askingNum = parseFloat(formData.asking_price);
    if (Number.isNaN(costNum) || costNum <= 0) {
      setError('Please enter a valid cost (number greater than 0).');
      return;
    }
    if (Number.isNaN(askingNum) || askingNum <= 0) {
      setError('Please enter a valid asking price (number greater than 0).');
      return;
    }
    if (!(formData.brand || '').trim() || !(formData.model || '').trim()) {
      setError('Brand and model are required.');
      return;
    }

    setLoading(true);

    try {
      // Convert numeric fields
      const watchData = {
        ...formData,
        year: formData.year ? parseInt(formData.year, 10) : null,
        cost: costNum,
        asking_price: askingNum,
        commission_percentage: formData.commission_percentage ? parseFloat(formData.commission_percentage) : null,
        // Convert date strings to ISO datetime format
        consignment_agreement_date: formData.consignment_agreement_date
          ? new Date(formData.consignment_agreement_date + 'T00:00:00').toISOString()
          : null,
        consignment_end_date: formData.consignment_end_date
          ? new Date(formData.consignment_end_date + 'T00:00:00').toISOString()
          : null
      };

      // Remove empty string values
      Object.keys(watchData).forEach(key => {
        if (watchData[key] === '') {
          watchData[key] = null;
        }
      });

      const response = await watchAPI.create(watchData);
      const watchId = response.data.id;

      // If we have a stock image URL, use server-side download to bypass CORS
      if (stockImageUrl) {
        try {
          // Get API base URL
          const getServerURL = () => {
            if (process.env.REACT_APP_API_URL) {
              return process.env.REACT_APP_API_URL;
            }
            if (typeof window !== 'undefined' &&
                window.location.hostname !== 'localhost' &&
                window.location.hostname !== '127.0.0.1') {
              return 'https://watchdealer-crm-backend.onrender.com';
            }
            return 'http://localhost:8000';
          };

          // Use server-side download endpoint to bypass CORS restrictions
          const encodedImageUrl = encodeURIComponent(stockImageUrl);
          const token = localStorage.getItem('token');
          const uploadResponse = await axios.post(
            `${getServerURL()}/api/watch-lookup/download-stock-image/${watchId}?image_url=${encodedImageUrl}`,
            {},
            {
              headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (imgError) {
          console.error('[Stock Image] Failed to upload:', imgError);
          console.error('[Stock Image] Error details:', imgError.response?.data);
          // Don't fail the whole operation if image upload fails
          // The watch was created successfully, just log the error
        }
      }

      navigate(`/inventory/${watchId}`);
    } catch (err) {
      // Handle validation errors
      console.error('Error creating watch:', err);
      console.error('Error response:', err.response);

      let errorMessage = 'Failed to create watch';

      if (err.response?.data?.detail) {
        const detail = err.response?.data?.detail;

        if (Array.isArray(detail)) {
          errorMessage = detail.map(error => {
            const field = error.loc ? error.loc.join(' > ') : 'Unknown field';
            return `${field}: ${error.msg}`;
          }).join('\n');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail, null, 2);
        }
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/inventory')}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Add New Watch</h1>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Basic Information</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Brand <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., Rolex"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., Submariner"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reference Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="reference"
                  value={formData.reference}
                  onChange={handleReferenceChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                  placeholder="e.g., 116610LN (auto-fills from online data)"
                />
                {lookupLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-900 border-t-transparent"></div>
                  </div>
                )}
              </div>
              {lookupMessage && (
                <p className={`mt-2 text-sm ${
                  lookupMessage.startsWith('✓') ? 'text-green-600' :
                  lookupMessage.startsWith('ℹ') ? 'text-blue-600' :
                  'text-amber-600'
                }`}>
                  {lookupMessage}
                </p>
              )}
              {showVariants && variants.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-700">🤖 Chrono found these variants - pick yours:</p>
                  <div className="grid grid-cols-1 gap-3">
                    {variants.map((variant, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => selectVariant(variant)}
                        className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                      >
                        <div className="w-24 h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {variant.image_url ? (
                            <img
                              src={variant.image_url}
                              alt={variant.description || variant.title}
                              className="w-full h-full object-cover rounded-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const icon = document.createElement('div');
                                icon.className = 'text-5xl';
                                icon.textContent = '⌚';
                                e.target.parentElement.appendChild(icon);
                              }}
                            />
                          ) : (
                            <div className="text-5xl">⌚</div>
                          )}
                        </div>
                        <div className="flex-1">
                          {variant.model && (
                            <p className="font-bold text-gray-900 group-hover:text-blue-700">
                              {variant.model}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {variant.description || variant.title}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            {variant.size && (
                              <span className="px-2 py-1 bg-gray-100 rounded-md text-gray-700">
                                📏 {variant.size}
                              </span>
                            )}
                            {variant.material && (
                              <span className="px-2 py-1 bg-gray-100 rounded-md text-gray-700">
                                💎 {variant.material}
                              </span>
                            )}
                            {variant.dial_color && (
                              <span className="px-2 py-1 bg-gray-100 rounded-md text-gray-700">
                                🎨 {variant.dial_color}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {stockImageUrl && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Stock Image Preview:</p>
                  <div className="relative w-full h-64 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200">
                    <img
                      src={stockImageUrl}
                      alt="Stock watch preview"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden absolute inset-0 items-center justify-center text-gray-400">
                      <p>Image unavailable</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    This is a reference image. Upload actual watch photos below.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                min="1800"
                max="2100"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., 2020"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Serial Number
              </label>
              <input
                type="text"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="Unique serial number"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Condition <span className="text-red-500">*</span>
              </label>
              <select
                name="condition"
                value={formData.condition}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              >
                <option value="New">New</option>
                <option value="Excellent">Excellent</option>
                <option value="Very Good">Very Good</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder="Detailed description of the watch..."
            />
          </div>

          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Internal Notes <span className="text-gray-500 text-xs">(Private - not shown to customers)</span>
            </label>
            <textarea
              name="internal_notes"
              value={formData.internal_notes || ''}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border-2 border-amber-100 bg-amber-50/30 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Private notes for staff use only (e.g., sourcing info, condition issues, special pricing notes...)"
            />
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Technical Specifications</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Case Size
              </label>
              <input
                type="text"
                name="case_size"
                value={formData.case_size}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., 40mm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Case Material
              </label>
              <input
                type="text"
                name="case_material"
                value={formData.case_material}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., Stainless Steel"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Movement
              </label>
              <input
                type="text"
                name="movement"
                value={formData.movement}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., Automatic"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Bracelet Material
              </label>
              <input
                type="text"
                name="bracelet_material"
                value={formData.bracelet_material}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., Stainless Steel"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Movement Type
              </label>
              <input
                type="text"
                name="movement_type"
                value={formData.movement_type}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., Caliber 3235"
              />
            </div>
          </div>
        </div>

        {/* Pricing & Accessories */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Pricing & Accessories</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Cost <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asking Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  name="asking_price"
                  value={formData.asking_price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Box
              </label>
              <select
                name="box"
                value={formData.box}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              >
                <option value="">Select...</option>
                <option value="Full Set">Full Set</option>
                <option value="Box Only">Box Only</option>
                <option value="No Box">No Box</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Papers
              </label>
              <select
                name="papers"
                value={formData.papers}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          {/* Public/Private Toggle */}
          <div className="mt-6 p-4 bg-gradient-to-r from-slate-50 to-stone-50 rounded-xl border-2 border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {formData.is_public ? (
                  <Eye className="w-5 h-5 text-slate-900" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-600" />
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-900">
                    Make this watch public
                  </label>
                  <p className="text-xs text-gray-600">
                    {formData.is_public
                      ? 'Other dealers can see this watch in the marketplace'
                      : 'Only you can see this watch in your inventory'}
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-slate-900"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Location</h2>
          <p className="text-sm text-gray-600 mb-6">
            Where is this watch physically located? (Optional - if not specified, dealer location will be used)
          </p>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., New York"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., NY"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                placeholder="e.g., USA"
              />
            </div>
          </div>
        </div>

        {/* Consignment Information */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Consignment Information</h2>

          {/* Consignment Toggle */}
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-100">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  This is a consignment watch
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  {formData.is_consignment
                    ? 'Track owner information and commission'
                    : 'This watch is owned by you'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_consignment"
                  checked={formData.is_consignment}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-amber-600"></div>
              </label>
            </div>
          </div>

          {/* Consignment Fields - Only show if is_consignment is true */}
          {formData.is_consignment && (
            <div className="space-y-6">
              {/* Owner Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Owner Name
                    </label>
                    <input
                      type="text"
                      name="consignment_owner_name"
                      value={formData.consignment_owner_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="Watch owner's name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Owner Email
                    </label>
                    <input
                      type="email"
                      name="consignment_owner_email"
                      value={formData.consignment_owner_email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="owner@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Owner Phone
                    </label>
                    <input
                      type="tel"
                      name="consignment_owner_phone"
                      value={formData.consignment_owner_phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Commission %
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="commission_percentage"
                        value={formData.commission_percentage}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="15.00"
                      />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Owner Address
                    </label>
                    <textarea
                      name="consignment_owner_address"
                      value={formData.consignment_owner_address}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder="Owner's full address..."
                    />
                  </div>
                </div>
              </div>

              {/* Agreement Dates */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Agreement Dates</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Agreement Start Date
                    </label>
                    <input
                      type="date"
                      name="consignment_agreement_date"
                      value={formData.consignment_agreement_date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Agreement End Date
                    </label>
                    <input
                      type="date"
                      name="consignment_end_date"
                      value={formData.consignment_end_date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Consignment Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Consignment Notes
                </label>
                <textarea
                  name="consignment_notes"
                  value={formData.consignment_notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Any additional notes about the consignment agreement..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/inventory')}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Create Watch
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddWatchPage;
